import { makeAutoObservable } from 'mobx';

import { ConfirmService } from '@/dmdrive/core/service/ConfirmService';
import { DriveAPI } from '@/dmdrive/drive/api/DriveAPI';
import { DriveItemType } from '@/dmdrive/drive/constants/driveItemType';
import { DriveFolder } from '@/dmdrive/drive/entities/DriveFolder';
import { DriveModel } from '@/dmdrive/drive/entities/DriveModel';
import { CreateProjectValues, UpdateProjectValues } from '@/dmdrive/drive/types/ProjectValues';
import { NotifyService } from '@/dmdrive/topic/services/NotifyService';

export class DriveStore {
  isFetching = true;
  projectsList: DriveModel[] = [];

  isCreateProjectModalOpen = false;

  isUploadingModel = false;

  private driveAPI: DriveAPI;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    this.driveAPI = new DriveAPI();
  }

  *init() {
    try {
      this.isFetching = true;

      this.projectsList = yield this.fetchProjects();
    } catch (e) {
      NotifyService.error(e);
    } finally {
      this.isFetching = false;
    }
  }

  *createProject(values: CreateProjectValues) {
    let conference: DriveModel | null = null;

    try {
      this.isUploadingModel = true;

      const { title, plan, result } = values;

      if (!plan && !result) {
        throw new Error('Two model are required to create project!');
      }

      yield this.driveAPI.uploadFile(plan!);
      yield this.driveAPI.uploadFile(result!);

      const projects: DriveModel[] = yield this.fetchProjects();

      const targetPlan = projects.find((element) => element.title === plan?.name) as DriveModel | undefined;
      const targetResult = projects.find((element) => element.title === result?.name) as DriveModel | undefined;

      if (targetPlan && targetResult) {
        conference = yield this.driveAPI.createDMIndustrialConference(
          targetPlan.conferenceId,
          targetResult.conferenceId,
        );

        if (conference) {
          if (title) {
            yield this.driveAPI.updateProject(conference.id, { title });
          }

          yield this.driveAPI.deleteProject(targetPlan.id);
          yield this.driveAPI.deleteProject(targetResult.id);
        }
      }

      yield this.init();
    } catch (e) {
      NotifyService.error(e);
    } finally {
      this.isUploadingModel = false;
    }

    return conference;
  }

  *updateFolder(id: number, values: UpdateProjectValues) {
    try {
      const [folder]: DriveModel[] = yield this.driveAPI.updateProject(id, values);

      const targetIndex = this.projectsList.findIndex((project) => project.id === id);

      if (targetIndex !== -1) {
        this.projectsList[targetIndex].title = folder.title;
      }
    } catch (e) {
      NotifyService.error(e);
    }
  }

  *removeProject(id: number) {
    try {
      const project = this.projectsList.find((element) => element.id === id);
      const shouldRemove: boolean = yield ConfirmService.deleteProject(project?.title);

      if (shouldRemove) {
        yield this.driveAPI.deleteProject(id);

        this.init();
      }
    } catch (e) {
      NotifyService.error(e);

      throw e;
    }
  }

  openCreateProjectModal() {
    this.isCreateProjectModalOpen = true;
  }

  closeCreateProjectModal() {
    this.isCreateProjectModalOpen = false;
  }

  dispose() {
    this.isFetching = true;
    this.projectsList = [];

    this.isUploadingModel = false;
  }

  private *fetchProjects() {
    const structure: DriveFolder = yield this.driveAPI.fetchStructure();

    const folders = structure?.children?.filter((item) => item.type !== DriveItemType.Folder) ?? [];

    folders.sort((a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf());

    return folders as DriveModel[];
  }
}
